// Messages Auto Draft (private beta `auto_draft`) — Messages → Settings → Auto Draft.
//
// Account-wide switch + send delay. Mounted only behind the strict beta route
// wrapper (PrivatePropertyBetaRoutes → BetaFeatureRoute), so this page and its
// query never run before the grant is confirmed. Server contract:
// server/routes/auto-draft.ts.

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { AutoDraftActivityCard, AUTO_DRAFT_ACTIVITY_KEY } from "./app-messages/AutoDraftActivityCard";

type SendDelay = "5" | "15" | "30" | "60" | "never";

type AutoDraftSettingsResponse = {
  enabled: boolean;
  sendDelay: SendDelay;
  ownerGranted: boolean;
  canEdit: boolean;
  cancelledDrafts?: number;
};

export const AUTO_DRAFT_SETTINGS_KEY = ["/api/app/messages/auto-draft/settings"] as const;

const DELAY_OPTIONS: Array<{ value: SendDelay; label: string; hint: string }> = [
  { value: "5", label: "5 minutes", hint: "Sends 5 minutes after the draft is written" },
  { value: "15", label: "15 minutes", hint: "Sends 15 minutes after the draft is written" },
  { value: "30", label: "30 minutes", hint: "Sends 30 minutes after the draft is written" },
  { value: "60", label: "60 minutes", hint: "Sends an hour after the draft is written" },
  { value: "never", label: "Never", hint: "Drafts wait until someone on your team sends them" },
];

export default function AppMessagesAutoDraft() {
  usePageTitle("Auto Draft");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const settingsQuery = useQuery<AutoDraftSettingsResponse>({ queryKey: AUTO_DRAFT_SETTINGS_KEY });
  const [enabled, setEnabled] = useState(false);
  const [sendDelay, setSendDelay] = useState<SendDelay>("never");

  // Seed the form once from the server; later refetches don't clobber edits.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !settingsQuery.data) return;
    setEnabled(settingsQuery.data.enabled);
    setSendDelay(settingsQuery.data.sendDelay);
    setSeeded(true);
  }, [seeded, settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (input: { enabled: boolean; sendDelay: SendDelay }) => {
      const res = await apiRequest("PUT", "/api/app/messages/auto-draft/settings", input);
      return (await res.json()) as AutoDraftSettingsResponse;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(AUTO_DRAFT_SETTINGS_KEY, data);
      setEnabled(data.enabled);
      setSendDelay(data.sendDelay);
      const cancelled = data.cancelledDrafts ?? 0;
      toast({
        title: "Auto Draft saved",
        description: !data.enabled && cancelled > 0
          ? `Auto Draft is off. ${cancelled} waiting draft${cancelled === 1 ? " was" : "s were"} cancelled.`
          : data.enabled ? "New member messages will get an AI draft." : "Auto Draft is off.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: AUTO_DRAFT_ACTIVITY_KEY });
    },
    onError: (err) => {
      toast({ title: "Couldn't save Auto Draft", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const data = settingsQuery.data;
  const canEdit = data?.canEdit === true;
  const dirty = !!data && (data.enabled !== enabled || data.sendDelay !== sendDelay);

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl p-6">
        <Link href="/app/messages" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700" data-testid="link-back-messages">
          <ArrowLeft className="h-4 w-4" /> Messages
        </Link>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand" />
              <h1
                className="text-xl font-semibold text-gray-900"
                data-testid="text-auto-draft-heading"
                data-tour="messages-auto-draft-heading"
              >
                Auto Draft
              </h1>
              <Badge variant="secondary">Beta</Badge>
            </div>
            <p className="text-sm text-gray-500">
              Let AI write a reply to every new member message, so an answer is ready in the chat before you open it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="messages-auto-draft" />
            <GuideButton pageKey="messages-auto-draft" />
          </div>
        </div>

        {settingsQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : settingsQuery.isError || !data ? (
          <Card className="p-6 text-sm text-red-600" data-testid="text-auto-draft-error">
            {parseErrorMessage(settingsQuery.error, "Couldn't load Auto Draft settings.")}
          </Card>
        ) : (
          <>
            {!data.ownerGranted && (
              <Card className="mb-4 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="text-auto-draft-owner-no-access">
                Drafts only run when the account owner has Auto Draft access. Ask the account owner to request it.
              </Card>
            )}
            <Card className="mb-4 p-6" data-testid="card-auto-draft-settings" data-tour="messages-auto-draft-settings">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="auto-draft-enabled" className="text-base font-semibold text-gray-900">
                    Write auto-drafts
                  </Label>
                  <p className="mt-1 text-sm text-gray-600">
                    A draft is written for every incoming member message. It appears in that member's chat, labelled as an auto-draft.
                  </p>
                </div>
                <Switch
                  id="auto-draft-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  disabled={!canEdit || saveMutation.isPending}
                  data-testid="switch-auto-draft-enabled"
                />
              </div>

              <div className="mt-6 border-t border-gray-100 pt-5">
                <h2 className="mb-1 text-sm font-semibold text-gray-900">Send delay</h2>
                <p className="mb-3 text-sm text-gray-600">
                  Drafts send automatically after this delay unless someone edits or cancels them first.
                </p>
                <RadioGroup
                  value={sendDelay}
                  onValueChange={(v) => setSendDelay(v as SendDelay)}
                  disabled={!canEdit || saveMutation.isPending}
                  className="grid gap-2 sm:grid-cols-2"
                  data-testid="radio-auto-draft-delay"
                >
                  {DELAY_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      htmlFor={`auto-draft-delay-${o.value}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${sendDelay === o.value ? "border-brand bg-brand/5" : "border-gray-200"}`}
                    >
                      <RadioGroupItem value={o.value} id={`auto-draft-delay-${o.value}`} className="mt-0.5" data-testid={`radio-auto-draft-delay-${o.value}`} />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">{o.label}</span>
                        <span className="block text-xs text-gray-500">{o.hint}</span>
                      </span>
                    </label>
                  ))}
                </RadioGroup>
                {sendDelay === "never" && (
                  <p className="mt-3 text-sm text-gray-600" data-testid="text-auto-draft-never">
                    With “Never”, drafts wait in the chat until someone sends them.
                  </p>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                {!canEdit && (
                  <span className="text-xs text-gray-500" data-testid="text-auto-draft-read-only">
                    Only the account owner or teammates who can edit Messages can change this.
                  </span>
                )}
                <Button
                  onClick={() => saveMutation.mutate({ enabled, sendDelay })}
                  disabled={!canEdit || !dirty || saveMutation.isPending}
                  data-testid="button-auto-draft-save"
                >
                  {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save
                </Button>
              </div>
            </Card>

            <AutoDraftActivityCard />

            <Card className="p-6" data-testid="card-auto-draft-rules-first" data-tour="messages-auto-draft-rules-first">
              <div className="mb-2 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand" />
                <h2 className="text-sm font-semibold text-gray-900">Message rules always come first</h2>
              </div>
              <ul className="list-disc space-y-1.5 pl-5 text-sm text-gray-600">
                <li>If one of your message rules replies to or queues a message for a member, no auto-draft is made.</li>
                <li>A waiting draft is cancelled when you or a teammate reply, a rule sends a reply, the conversation is resolved, or the member moves out.</li>
                <li>If the member writes again before the draft sends, it is replaced with a new draft that covers all their messages, and the delay starts over.</li>
                <li>No draft is made when a reply isn't needed, for example when the member only says “thanks”.</li>
                <li>Turning Auto Draft off cancels every waiting draft.</li>
              </ul>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
